import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState } from '@/components/ui';
import { isChunkLoadError, isStale } from '@/lib/new-version';
import { useSession } from './session';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

/**
 * The technical half of a failure, folded away.
 *
 * The screen used to print `error.message` as the explanation — "Cannot read
 * properties of undefined (reading 'map')" — in English, to a receptionist.
 * That text is still worth having: it is what a developer needs from a
 * screenshot. So it stays on the page, behind „Detalii”, under a sentence the
 * person in front of it can act on.
 */
function TechnicalDetail({ text }: { text: string }) {
  return (
    <details style={{ marginTop: 'var(--s-3)', maxWidth: '100%' }}>
      <summary style={{ cursor: 'pointer' }}>Detalii</summary>
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: 12,
          color: 'var(--text-muted)',
          marginTop: 'var(--s-2)',
        }}
      >
        {text}
      </pre>
    </details>
  );
}

/**
 * Catches a render error anywhere below it.
 *
 * Without one, a single component throwing unmounts the whole tree and the
 * user is left with a blank white page — which, in a clinic mid-appointment,
 * looks exactly like the application being gone.
 */
class Boundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept in the console rather than sent anywhere: the page may contain
    // patient data, and an error reporter is a route out of the building for
    // it. Wire one up only with a scrubber in front.
    console.error('Render error:', error, info.componentStack);

    // A hard chunk-load failure means the screen a deploy replaced never
    // rendered — there is no draft on it to protect — so we reload once, on our
    // own, instead of leaving „reîncarcă” to be clicked after every deploy. The
    // passive stale banner still never auto-reloads (a working screen may hold a
    // draft). Guarded so a chunk that is genuinely gone cannot loop the page:
    // one reload per 20s.
    if (isChunkLoadError(error)) {
      try {
        const KEY = 'df-chunk-reload-at';
        const last = Number(sessionStorage.getItem(KEY) ?? 0);
        if (Date.now() - last > 20_000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
        }
      } catch {
        // sessionStorage blocked (private mode): fall through to the manual screen.
      }
    }
  }

  override render() {
    const { error } = this.state;
    // A screen whose file a deploy replaced while the page was open: not a
    // fault, and reloading is the whole fix — so say that, not „eroare”.
    if (error && (isChunkLoadError(error) || isStale())) {
      return (
        <div style={{ padding: 'var(--s-6)' }}>
          <ErrorState
            title="A apărut o versiune nouă a aplicației"
            message="Ecranul acesta s-a schimbat de când ai deschis pagina. Reîncarcă pagina ca să-l deschizi."
            onRetry={() => window.location.reload()}
          />
        </div>
      );
    }
    if (error) {
      return (
        <div style={{ padding: 'var(--s-6)' }}>
          <ErrorState
            title={i18n.t('common:aplicatiaAIntampinat')}
            message={i18n.t('common:oParteAEcranului')}
            onRetry={() => window.location.reload()}
          />
          <TechnicalDetail text={`${error.name}: ${error.message}`} />
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * What the browser says when it never reached the server — not a sentence the
 * API wrote, and not Romanian: Chrome's "Failed to fetch", Firefox's
 * "NetworkError when attempting to fetch resource.", Safari's "Load failed",
 * and a proxy's HTML page read as JSON.
 */
const BROWSER_FAILURE = /^(Failed to fetch|NetworkError|Load failed|Unexpected token|JSON\.parse)/i;

/**
 * The startup gate.
 *
 * Until the clinic is resolved there is nothing to render — not even a login
 * form, because the form carries the clinic's name and colour.
 */
export function AppBoundary({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const { status, error } = useSession();

  if (status === 'error') {
    // The server's refusals — no clinic at this address, access suspended,
    // database unavailable — are Romanian and say what happened, so they are
    // shown as they are. Only the browser's own English goes behind „Detalii”.
    const fromBrowser = error ? BROWSER_FAILURE.test(error) : false;
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 'var(--s-6)' }}>
        <div>
          <ErrorState
            title={t('clinicaNuAPutut')}
            message={fromBrowser ? t('serverulNuAPutut') : (error ?? t('acestDomeniuNuEste'))}
            onRetry={() => window.location.reload()}
          />
          {fromBrowser && error ? <TechnicalDetail text={error} /> : null}
        </div>
      </div>
    );
  }

  return <Boundary>{children}</Boundary>;
}
